import type { ReactNode } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appMediaCopy from '@/i18n/ns/app-media';
import { captureCounts, type ManagedImageData, type ObservedImage } from './media-model';
import './media.css';

type FormatAt = (iso: string) => string;

/** APP's shared SDK Image, injected: `(data, size) => the image in its declared ratio box`. */
export type RenderImage = (
  data: ManagedImageData,
  size: 'tile' | 'thumb',
  /** Where it is drawn (`dataImageOwner` etc.): the viewer's identity for this image. */
  owner: string,
) => ReactNode;

/** An image as observed at one point: the shared image, then "{source} · as seen {at}", then the
 * quiet "Image changed since" note when today's differs. */
export function ObservedImageBlock(props: {
  image: ObservedImage;
  renderImage: RenderImage;
  formatAt: FormatAt;
  owner: string;
}) {
  const copy = useNamespaceCopy(appMediaCopy);
  const { image } = props;
  const { ref } = image;
  const at = ref.observedAt ? props.formatAt(ref.observedAt) : null;
  return (
    <figure className="pc-media-observed" data-testid="media-observed">
      {props.renderImage(image, 'tile', props.owner)}
      <figcaption className="pc-media-provenance" data-testid="media-provenance">
        {at !== null &&
          (ref.sourceLabel
            ? formatCopy(copy.provenance, { source: ref.sourceLabel, at })
            : formatCopy(copy.provenanceNoSource, { at }))}
        {image.changedSince === true && (
          <span className="pc-media-changed" data-testid="media-changed">
            {copy.changedSince}
          </span>
        )}
      </figcaption>
    </figure>
  );
}

/** "Showing 18 of 24 images · 6 not available". Missing is counted, never silently dropped. */
export function MediaCaptureLine(props: { shown: number; total: number }) {
  const copy = useNamespaceCopy(appMediaCopy);
  const counts = captureCounts(props.shown, props.total);
  if (!counts) return null;
  return (
    <p className="pc-media-capture" data-testid="media-capture">
      {formatCopy(copy.captureLine, {
        n: String(counts.shown),
        total: String(props.total),
        m: String(counts.missing),
      })}
    </p>
  );
}
